import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import ApiOptionsConfig from './api-options.config';
import { ApiBaseService } from './api-base.service';
import {
  RegulatorPayload,
  RegulatorClientPayload,
  RegulatorWebsitePayload,
  VltPayload
} from '../../models/entities/regulator';

const BASE_URL = '/regulators';

@Injectable({
  providedIn: 'root',
})
export class RegulatorsService extends ApiBaseService {

  /*****************
    REGULATORS
  *****************/

  list(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL), options?.params);
  }

  detail(regulatorId: number): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${regulatorId}`));
  }

  create(data: RegulatorPayload): Observable<any> {
    return this.POST(this.getUrl(BASE_URL), data);
  }

  update(regulatorId: number, data: RegulatorPayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/${regulatorId}`), data);
  }

  /** Used by the data-table delete button (idKey `regulatorId`). */
  delete(regulatorId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${regulatorId}`));
  }

  listChecks(regulatorId: number): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${regulatorId}/checks`));
  }

  syncChecks(regulatorId: number): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/${regulatorId}/checks/sync`), null);
  }

  /*****************
    CLIENTS (per regulator)
  *****************/

  /** Clients of a regulator, including those without websites. */
  listClients(regulatorId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${regulatorId}/clients`), options?.params);
  }

  detailClient(regulatorId: number, clientId: number): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${regulatorId}/clients/${clientId}`));
  }

  createClient(regulatorId: number, data: RegulatorClientPayload): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/${regulatorId}/clients`), data);
  }

  updateClient(regulatorId: number, clientId: number, data: RegulatorClientPayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/${regulatorId}/clients/${clientId}`), data);
  }

  deleteClient(regulatorId: number, clientId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${regulatorId}/clients/${clientId}`));
  }

  /*****************
    WEBSITE COMPLIANCE
  *****************/

  listClientWebsites(regulatorId: number, clientId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${regulatorId}/clients/${clientId}/websites`), options?.params);
  }

  createWebsite(regulatorId: number, data: RegulatorWebsitePayload): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/${regulatorId}/websites`), data);
  }

  updateWebsite(regulatorId: number, websiteId: number, data: RegulatorWebsitePayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/${regulatorId}/websites/${websiteId}`), data);
  }

  deleteWebsite(regulatorId: number, websiteId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${regulatorId}/websites/${websiteId}`));
  }

  /*****************
    SCANS
  *****************/

  startScan(regulatorId: number, clientId: number, websiteId: number): Observable<any> {
    return this.POST(this.getUrl(this.scansPath(regulatorId, clientId, websiteId)), null);
  }

  listScans(regulatorId: number, clientId: number, websiteId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(this.scansPath(regulatorId, clientId, websiteId)), options?.params);
  }

  detailScan(regulatorId: number, clientId: number, websiteId: number, scanId: number): Observable<any> {
    return this.GET(this.getUrl(`${this.scansPath(regulatorId, clientId, websiteId)}/${scanId}`));
  }

  scanScreenshot(regulatorId: number, clientId: number, websiteId: number, scanId: number): Observable<Blob> {
    return this.GETBLOB(this.getUrl(`${this.scansPath(regulatorId, clientId, websiteId)}/${scanId}/screenshot`));
  }

  scanReport(regulatorId: number, clientId: number, websiteId: number, scanId: number): Observable<Blob> {
    return this.GETBLOB(this.getUrl(`${this.scansPath(regulatorId, clientId, websiteId)}/${scanId}/report`));
  }

  /*****************
    VLT
  *****************/

  listVlts(regulatorId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${regulatorId}/vlts`), options?.params);
  }

  detailVlt(regulatorId: number, vltId: number): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${regulatorId}/vlts/${vltId}`));
  }

  createVlt(regulatorId: number, data: VltPayload): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/${regulatorId}/vlts`), data);
  }

  updateVlt(regulatorId: number, vltId: number, data: VltPayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/${regulatorId}/vlts/${vltId}`), data);
  }

  deleteVlt(regulatorId: number, vltId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${regulatorId}/vlts/${vltId}`));
  }

  private scansPath(regulatorId: number, clientId: number, websiteId: number): string {
    return `${BASE_URL}/${regulatorId}/clients/${clientId}/websites/${websiteId}/scans`;
  }
}
